/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import config from 'js/app/config';
import useRouter from 'js/lib/useRouter';

import { Button } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { getUrl, onClickNavUrl } from 'bundles/cds-labs/components/Modules/utils/utm';
import { NativeDataContext } from 'bundles/ent-website/components/NativeDataContext';
import type { LinkIndividualLink } from 'bundles/ent-website/contentful/core/Navigation';
import { eventingTransform } from 'bundles/ent-website/utils/eventingTransform';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/ent-website';

export const PRIORITY_CTAS = {
  C4T: 'C4T',
  DEFAULT: 'DEFAULT',
} as const;

type BaseProps = {
  primaryCta: LinkIndividualLink | undefined | null;
  href: string;
};

type Props = BaseProps & {
  priorityCtaOverride: keyof typeof PRIORITY_CTAS;
};

function DefaultCTA(props: BaseProps) {
  const { ctaTarget, ctaText, shouldOpenInNewWindow } = props?.primaryCta ?? {};

  const defaultHref = props.href;
  const router = useRouter();
  const styles: Record<string, Interpolation<Theme>> = {
    button: () => css`
      display: inline-block;
      margin-inline-start: var(--cds-spacing-200);
    `,
  };

  const trackingNamePriorityCtaOverride = eventingTransform('navigation_priority_cta');

  return (
    <div css={styles.button}>
      <Button
        data-testid="ew_header_cta"
        trackingName={trackingNamePriorityCtaOverride}
        component={TrackedA}
        variant="primary"
        size="small"
        target={shouldOpenInNewWindow ? '_blank' : undefined}
        href={getUrl(router, ctaTarget || defaultHref)}
      >
        {ctaText ?? _t('Contact Sales')}
      </Button>
    </div>
  );
}

function CourseraForTeamsCTA() {
  const router = useRouter();
  const PURCHASE_URL = `${config.url.base}purchase/plan`;

  const styles: Record<string, Interpolation<Theme>> = {
    button: () => css`
      display: inline-block;
      margin-inline-start: var(--cds-spacing-200);
    `,
  };

  const { getNativeData } = React.useContext(NativeDataContext);
  const promo = getNativeData('promo');
  const trackingNamePriorityCtaOverride = eventingTransform('navigation_priority_c4t_cta');

  const params: Record<string, string> = {};
  if (promo?.promotion) {
    params.promoCode = promo.promoCode;
  }

  return (
    <div css={styles.button}>
      <Button
        data-testid="ew_header_cta"
        trackingName={trackingNamePriorityCtaOverride}
        component={TrackedA}
        variant="primary"
        size="small"
        href={getUrl(router, PURCHASE_URL, params)}
        onClick={onClickNavUrl(router, PURCHASE_URL, params)}
      >
        Buy Now
      </Button>
    </div>
  );
}

/*
The href prop is required for a fallback value for non data driven headers.
This will ideally be removed in the near future after experiments such as 
the c4b promo and the c4t link are concluded and folded into our header.
*/
function PriorityCtaOverride({ priorityCtaOverride, primaryCta, href }: Props) {
  switch (priorityCtaOverride) {
    case PRIORITY_CTAS.C4T:
      return <CourseraForTeamsCTA />;
    case PRIORITY_CTAS.DEFAULT:
      return <DefaultCTA primaryCta={primaryCta} href={href} />;
    default:
      return null;
  }
}

export default PriorityCtaOverride;
